/**
 * Centralized cell-content resolution — a single function that handles
 * the three ways a DataGrid column can render its content :
 *
 *   1. `col.cell(row)`         : explicit JSX override (avatars, chips, …)
 *   2. `col.dataType` + accessor : typed renderer via `renderTypedCell`
 *   3. `col.accessor` alone    : untyped fallback (string/date/null)
 *
 * Before this module, the same cascade was repeated verbatim in
 * `DataRowImpl` and in the ellipsis-title logic, which was both verbose
 * and error-prone whenever a new data type was added.
 *
 * Also provides `readPlainText(col, row)` — the "flat text" variant used
 * for tooltip `title` attributes and accessible labels.
 */

import type { ReactNode } from 'react';
import type { ColumnAccessor, DataGridColumn } from './types';
import { renderTypedCell } from './column-types';

/**
 * Read the raw accessor value from a row. `sortValue` is intentionally
 * NOT checked here — that path is reserved for the sort comparator.
 * Null-like values and non-primitive types bubble up as `undefined`.
 */
export function readAccessor<T>(
  accessor: ColumnAccessor<T>,
  row: T
): string | number | Date | null | undefined {
  if (typeof accessor === 'function') return accessor(row);
  const value = row[accessor];
  if (
    value === null ||
    value === undefined ||
    typeof value === 'string' ||
    typeof value === 'number' ||
    value instanceof Date
  ) {
    return value as string | number | Date | null | undefined;
  }
  return undefined;
}

/**
 * Default rendering for an untyped value — used when the column has
 * neither a `cell` override nor a `dataType`. Null-like → `'—'`,
 * dates → French locale date, everything else → `String()`.
 */
function renderUntypedValue(value: string | number | Date | null | undefined): ReactNode {
  if (value === null || value === undefined) return '—';
  if (value instanceof Date) return value.toLocaleDateString('fr-FR');
  return String(value);
}

/**
 * Resolve the visible content of a cell for a given row. Respects the
 * priority chain (cell → typed → untyped → null) in a single place.
 */
export function resolveCellContent<T>(
  col: DataGridColumn<T>,
  row: T,
  currency: string
): ReactNode {
  if (col.cell) return col.cell(row);

  if (col.accessor === undefined) return null;

  const raw = readAccessor(col.accessor, row);

  if (col.dataType) return renderTypedCell(col, raw, currency);
  return renderUntypedValue(raw);
}

/**
 * Plain-text version of a cell value — used for `title` tooltips on
 * ellipsis columns, CSV-ish fallbacks, or ARIA labels. Never returns
 * JSX. Dates are localized with both date and time so the tooltip
 * carries more info than the truncated display.
 */
export function readPlainText<T>(
  col: DataGridColumn<T>,
  row: T
): string | undefined {
  if (col.accessor === undefined) return undefined;
  const raw = readAccessor(col.accessor, row);
  if (raw === null || raw === undefined) return undefined;
  if (raw instanceof Date) return raw.toLocaleString('fr-FR');
  return String(raw);
}
